import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";

/** ── Palette loader (28-byte header, 256×(R,G,B) int32 BE) ───────────────── */
async function loadPalette(name) {
  let paletteName = String(name);
  if (!paletteName.startsWith("palette.")) paletteName = `palette.${paletteName}`;

  const res = await fetch(`/palettes/${paletteName}`);
  if (!res.ok) {
    console.error(`Failed to load palette file ${paletteName}: HTTP ${res.status}`);
    return Array.from({ length: 256 }, (_, i) => [i, i, i]);
  }

  const buf = await res.arrayBuffer();
  const view = new DataView(buf);
  const colors = [];
  let offset = 28;
  const needed = offset + 256 * 12;
  if (buf.byteLength < needed) {
    console.error(`Palette ${paletteName} too small (got ${buf.byteLength}, need ${needed})`);
    return Array.from({ length: 256 }, (_, i) => [i, i, i]);
  }
  for (let i = 0; i < 256; i++) {
    const r = view.getInt32(offset, false) & 0xff; offset += 4;
    const g = view.getInt32(offset, false) & 0xff; offset += 4;
    const b = view.getInt32(offset, false) & 0xff; offset += 4;
    colors.push([r, g, b]);
  }
  colors[0] = [0, 0, 0];
  return colors;
}

/** ── App ─────────────────────────────────────────────────────────────────── */
export default function App() {
  const canvasRef = useRef(null);
  const [ctx, setCtx] = useState(null);

  // Escape radius (Mandelbrot semantics)
  const ESCAPE_R = 2;
  const ESCAPE_R2 = ESCAPE_R * ESCAPE_R;

  // Canvas size
  const [a, setA] = useState(window.innerWidth);
  const [b, setB] = useState(Math.floor(window.innerHeight * 0.62));

  // Plane bounds
  const xmin = -2.25, xmax = 1.5;
  const ymin = -0.75, ymax = 0.75;

  // Iteration state
  const [c, setC] = useState(null);
  const [z, setZ] = useState({ re: 0, im: 0 });
  const [n, setN] = useState(0);
  // entries: { type:'z', index, value:{re,im}, color:string }
  const [list, setList] = useState([]);
  const [escaped, setEscaped] = useState(false);
  const [escapedAt, setEscapedAt] = useState(null);

  // Palette
  const [paletteName, setPaletteName] = useState("palette.5");
  const [palette, setPalette] = useState(() =>
    Array.from({ length: 256 }, (_, i) => [i, i, i])
  );

  // mini-map settings
  const miniRef = useRef(null);
  const [miniRes, setMiniRes] = useState(101);       // 101x101 pixels
  const [miniSpan, setMiniSpan] = useState(0.25);    // half-width in complex plane (±miniSpan)
  const [miniMaxIter, setMiniMaxIter] = useState(200);
  const [showMini, setShowMini] = useState(true);

  /** ── Helpers ───────────────────────────────────────────────────────────── */
  const pixelToComplex = (px, py) => ({
    re: xmin + (px / a) * (xmax - xmin),
    im: ymax - (py / b) * (ymax - ymin),
  });

  const complexToPixel = ({ re, im }) => ({
    px: ((re - xmin) / (xmax - xmin)) * a,
    py: ((ymax - im) / (ymax - ymin)) * b,
  });

  const inBounds = ({ re, im }) => re >= xmin && re <= xmax && im >= ymin && im <= ymax;

  const iterate = (z0, c0) => ({
    re: z0.re * z0.re - z0.im * z0.im + c0.re,
    // keep form consistent with your current app
    im: 2 * z0.re * z0.im + c0.im,
  });

  const fmt = (x) => {
    const ax = Math.abs(x);
    return (ax >= 1e6 || (ax !== 0 && ax < 1e-4))
      ? x.toExponential(3)
      : x.toFixed(6).replace(/0+$/,'').replace(/\.$/,'');
  };

  const colorForIteration = (k) => {
    const idx = Math.max(1, Math.min(255, k));
    const [r, g, b] = palette[idx] || [0, 0, 0];
    return `rgb(${r}, ${g}, ${b})`;
  };

  const clear = () => ctx && ctx.clearRect(0, 0, a, b);

  const drawAxes = () => {
    if (!ctx) return;
    ctx.save();
    ctx.strokeStyle = "rgba(0,0,0,0.35)";
    ctx.lineWidth = 1;
    const { py } = complexToPixel({ re: 0, im: 0 });
    ctx.beginPath(); ctx.moveTo(0, py + 0.5); ctx.lineTo(a, py + 0.5); ctx.stroke();
    const { px } = complexToPixel({ re: 0, im: 0 });
    ctx.beginPath(); ctx.moveTo(px + 0.5, 0); ctx.lineTo(px + 0.5, b); ctx.stroke();
    ctx.restore();
  };

  const drawDot = (point, radius = 3, color = "#000") => {
    if (!ctx) return;
    const { px, py } = complexToPixel(point);
    ctx.save();
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(px, py, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  };

  const redrawAll = () => {
    if (!ctx) return;
    clear();
    drawAxes();
    const zs = list
      .filter((e) => e.type === "z")
      .sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
    zs.forEach((e) => {
      if (inBounds(e.value)) drawDot(e.value, 3, e.color || "#000");
    });
  };

  /** ── Effects ───────────────────────────────────────────────────────────── */
  useEffect(() => {
    const onResize = () => {
      setA(window.innerWidth);
      setB(Math.floor(window.innerHeight * 0.62));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    cvs.width = a;
    cvs.height = b;
    setCtx(cvs.getContext("2d"));
  }, [a, b]);

  useEffect(() => {
    if (ctx) redrawAll();
  }, [ctx, list, c]);

  useEffect(() => {
    (async () => setPalette(await loadPalette(paletteName)))();
  }, [paletteName]);

  // If palette changes after an escape, recolor the on-canvas escaping point
  useEffect(() => {
    if (escapedAt == null) return;
    setList((prev) => {
      const i = prev.findIndex((e) => e.type === "z" && e.index === escapedAt);
      if (i === -1) return prev;
      const upd = [...prev];
      upd[i] = { ...upd[i], color: colorForIteration(escapedAt) };
      return upd;
    });
  }, [palette, escapedAt]);

  /** ── Mini-map ──────────────────────────────────────────────────────────── */
  const drawMiniMap = useCallback(() => {
    const cvs = miniRef.current;
    if (!cvs || !c || !palette) return;

    const size = miniRes;
    cvs.width = size;
    cvs.height = size;
    const mctx = cvs.getContext("2d");
    const img = mctx.createImageData(size, size);
    const pix = img.data;

    const minRe = c.re - miniSpan;
    const maxRe = c.re + miniSpan;
    const minIm = c.im - miniSpan;
    const maxIm = c.im + miniSpan;
    const ESC2 = 4;
    const palLen = palette.length;

    for (let py = 0; py < size; py++) {
      const cim = maxIm - (py / (size - 1)) * (maxIm - minIm);
      for (let px = 0; px < size; px++) {
        const cre = minRe + (px / (size - 1)) * (maxRe - minRe);
        let zr = 0, zi = 0, iter = 0;
        while (zr * zr + zi * zi <= ESC2 && iter < miniMaxIter) {
          const xr = zr * zr - zi * zi + cre;
          const yi = 2 * zr * zi + cim;
          zr = xr; zi = yi; iter++;
        }
        const idx = 4 * (py * size + px);
        if (iter === miniMaxIter) {
          pix[idx] = 0; pix[idx + 1] = 0; pix[idx + 2] = 0; pix[idx + 3] = 255;
        } else {
          const safe = Math.max(1, Math.min(iter, palLen - 1));
          const colorIdx = safe % palLen || 1;
          const [r, g, b] = palette[colorIdx] || [0, 0, 0];
          pix[idx] = r; pix[idx + 1] = g; pix[idx + 2] = b; pix[idx + 3] = 255;
        }
      }
    }

    mctx.putImageData(img, 0, 0);

    // crosshair at center
    mctx.save();
    mctx.strokeStyle = "rgba(255,255,255,0.9)";
    mctx.lineWidth = 1;
    const mid = Math.floor(size / 2);
    mctx.beginPath();
    mctx.moveTo(mid, mid - 4); mctx.lineTo(mid, mid + 4);
    mctx.moveTo(mid - 4, mid); mctx.lineTo(mid + 4, mid);
    mctx.stroke();
    mctx.restore();

    // thin border
    mctx.strokeStyle = "rgba(0,0,0,0.4)";
    mctx.strokeRect(0.5, 0.5, size - 1, size - 1);
  }, [c, palette, miniRes, miniSpan, miniMaxIter]);

  useEffect(() => { if (showMini) drawMiniMap(); }, [showMini, drawMiniMap, list]);

  /** ── Handlers ──────────────────────────────────────────────────────────── */
  // FIRST CLICK → draw z1 = c in BLACK (unless it escapes immediately, then color by k=1)
  const handleClick = (e) => {
    if (!ctx) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left, y = e.clientY - rect.top;
    const chosenC = pixelToComplex(x, y);

    // z0 = 0 → z1 = c
    const z1 = chosenC;
    const r2 = z1.re * z1.re + z1.im * z1.im;
    const escapedNow = r2 > ESCAPE_R2;

    setC(chosenC);
    setZ(z1);
    setN(1);
    setEscaped(escapedNow);
    setEscapedAt(escapedNow ? 1 : null);

    const color = escapedNow ? colorForIteration(1) : "#000000";
    setList([{ type: "z", index: 1, value: z1, color }]);
  };

  // NEXT → advance from current z_n
  const handleNext = () => {
    if (!c || escaped) return;

    const nextZ = iterate(z, c);
    const k = n + 1;
    const r2 = nextZ.re * nextZ.re + nextZ.im * nextZ.im;
    const vis = inBounds(nextZ);

    if (r2 > ESCAPE_R2) {
      if (vis) {
        setList((prev) => [...prev, { type: "z", index: k, value: nextZ, color: colorForIteration(k) }]);
        setZ(nextZ); setN(k);
      } else {
        // Escapes off-canvas → recolor the last *visible* iterate
        setList((prev) => {
          let targetListIdx = -1, highestVisibleIndex = -1;
          prev.forEach((e, i) => {
            if (e.type === "z" && inBounds(e.value) && e.index > highestVisibleIndex) {
              highestVisibleIndex = e.index; targetListIdx = i;
            }
          });
          if (targetListIdx !== -1) {
            const next = [...prev];
            next[targetListIdx] = { ...next[targetListIdx], color: colorForIteration(k) };
            return next;
          }
          return prev;
        });
      }
      setEscaped(true); setEscapedAt(k);
      return;
    }

    // Not escaped → draw next iterate in BLACK
    setList((prev) => [...prev, { type: "z", index: k, value: nextZ, color: "#000000" }]);
    setZ(nextZ); setN(k);
  };

  /** ── Dropdown helpers ─────────────────────────────────────────────────── */
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  useEffect(() => {
    setActiveIndex((i) => Math.min(i, Math.max(0, list.length - 1)));
  }, [list.length]);

  const entryText = (entry) => {
    if (entry.type === "z") {
      const { index, value } = entry;
      return `z${index} = ${fmt(value.re)} ${value.im >= 0 ? "+" : "−"} ${fmt(Math.abs(value.im))}i`;
    }
    return "—";
  };

  const selectedText = useMemo(
    () => (list[activeIndex] ? entryText(list[activeIndex]) : "—"),
    [list, activeIndex]
  );

  const onKeyDown = (e) => {
    if (!open && (e.key === "Enter" || e.key === " " || e.key === "ArrowDown")) {
      e.preventDefault(); setOpen(true); return;
    }
    if (open) {
      if (e.key === "Escape") { setOpen(false); return; }
      if (e.key === "ArrowDown") { e.preventDefault(); setActiveIndex((i) => Math.min(i + 1, list.length - 1)); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setActiveIndex((i) => Math.max(i - 1, 0)); return; }
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(false); return; }
    }
  };

  const onSelect = (idx) => { setActiveIndex(idx); setOpen(false); };

  /** ── Help modal (embedded) ─────────────────────────────────────────────── */

  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "h" || e.key === "H") setHelpOpen((v) => !v);
      if (e.key === "Escape") setHelpOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const HELP_CSS = `
/* Controls row and Help placement */
.controls { display:flex; flex-wrap:wrap; align-items:center; gap:8px; }
.controls .help-btn { margin-left:auto; }

/* Compact width for the first complex-values dropdown */
.dropdown.complex-select, .complex-select {
  inline-size: max-content;
  width: auto;
  min-inline-size: 16ch;
  max-inline-size: 42ch;
}
.dropdown.complex-select > .dropdown-button,
.dropdown.complex-select > button {
  inline-size: max-content;
  width: auto;
}

/* Help modal */
.help-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,.45); display: grid; place-items: center; z-index: 1000; }
.help-card { background: #fff; color: #111; width: min(800px, 92vw); max-height: 85vh; overflow: auto; padding: 20px 24px; border-radius: 12px; box-shadow: 0 10px 30px rgba(0,0,0,.25); }
.help-card h2 { margin: 0 0 12px; }
.help-card h3 { margin: 18px 0 6px; }
.help-card code, .help-card kbd { background: #f3f4f6; border-radius: 6px; padding: 0 .35em; }
.help-actions { margin-top: 16px; display: flex; justify-content: flex-end; }
.btn { padding: 6px 12px; border-radius: 8px; border: 1px solid #d1d5db; background: #fff; cursor: pointer; }
.btn:hover { background: #f9fafb; }
`;

  function HelpModal({ open, onClose }) {
    if (!open) return null;
    return (
      <div className="help-backdrop" role="dialog" aria-modal="true" aria-labelledby="help-title" onClick={onClose}>
        <div className="help-card" onClick={(e) => e.stopPropagation()}>
          <h2 id="help-title">How to use this app</h2>
            <p>This is a companion app to the fractals app presented in Lesson plan 10 and is intended to help you understand the connection between the color bands surrounding the Mandelbrot set and the complex numbers arising from iterations of the recursive function f(z) = z<sup>2</sup> + c as c is chosen by selecting points in the complex plane.</p><p> We start with z = 0, compute z = f(0) + c, then f(f(z)), f(f(f(z))), and continue until |z| > 2 (z lies outside the circle of radius 2). The number of iterations of f(z) it takes to escape will determine the color of the band. The color is defined by the color palette you have chosen. Try experimenting with palette.5 and palette.15.</p><p>When you tap on a point in the screen, its position in the complex plane will be reflected in the mini-map below. The <strong>half-span</strong> button acts like a zoom.</p>
          <h3>Pick a point <small>(the constant <code>c</code>)</small></h3>
          <p>Click the large plane to choose <code>c</code> (its coordinates appear in the list).</p>

          <h3>Iteration list (z₁, z₂, …)</h3>
          <p>The sequence follows <code>z<sub>{`{`}n+1{`}`}</sub> = z<sub>n</sub><sup>2</sup> + c</code>. Selecting an item highlights that iterate.</p>

          <h3>Next</h3>
          <p>Advances one iteration. The button disables once <code>|z|</code> exceeds the escape radius.</p>

          <h3>Palette</h3>
          <p>Choose a <code>palette.N</code> file. Colour reflects the iteration at which escape occurs. Interior points remain black.</p>

          <h3>Mini-map</h3>
          <ul>
            <li><b>Resolution</b>: mini-map pixel size.</li>
            <li><b>Half-span (±)</b>: world-space half-width around the crosshair.</li>
            <li><b>Max iter</b>: iteration cap for the mini-map render.</li>
            <li><b>Recompute</b>: re-render with the current settings.</li>
          </ul>

          <h3>Shortcuts</h3>
          <ul>
            <li><kbd>H</kbd>: Toggle Help</li>
            <li><kbd>Esc</kbd>: Close Help</li>
          </ul>

          <div className="help-actions">
            <button className="btn" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  /** ── Render ───────────────────────────────────────────────────────────── */
  return (
    <div style={{ fontFamily: "system-ui, sans-serif", width: "100%" }}>
      <style>{HELP_CSS}</style>

      {/* Upper labels */}
      <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 12px" }}>
        <span>(-2.25, 0.75)</span> <span>(1.5, 0.75)</span>
      </div>

      {/* Canvas */}
      <canvas
        ref={canvasRef}
        onClick={handleClick}
        style={{ border: "1px solid #333", display: "block", margin: "0 auto", width: "100%", height: b }}
      />

      {/* Lower labels */}
      <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 12px" }}>
        <span>(-2.25, -0.75)</span> <span>(1.5, -0.75)</span>
      </div>

      {/* Controls — single row */}
      <div
        className="controls"
        style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 12px" }}
      >
        <label style={{ fontWeight: 600, marginRight: 8, whiteSpace: "nowrap" }}>
          f(z) = z² + c
        </label>

        {/* Results dropdown — compact width */}
        <div
          className="dropdown complex-select"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          tabIndex={0}
          onKeyDown={onKeyDown}
          style={{ flex: "0 0 auto" }}
        >
          <div className="dropdown-button" onClick={() => setOpen((o) => !o)}>
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {list[activeIndex] ? (
                <span
                  className="dot"
                  style={{
                    width: 12, height: 12, borderRadius: "50%",
                    background: list[activeIndex].color || "#000",
                  }}
                />
              ) : (
                <span className="dot" style={{ width: 12, height: 12, borderRadius: "50%", background: "#000" }} />
              )}
              <span style={{ color: "#000" }}>{selectedText}</span>
            </span>
            <span aria-hidden>▾</span>
          </div>

          {open && (
            <div className="dropdown-list" role="listbox">
              {list.map((entry, i) => (
                <div
                  key={i}
                  role="option"
                  aria-selected={i === activeIndex}
                  className="dropdown-item"
                  onClick={() => onSelect(i)}
                >
                  <span
                    className="dot"
                    style={{
                      width: 12, height: 12, borderRadius: "50%",
                      background: entry.color || "#000",
                    }}
                  />
                  <span style={{ color: "#000" }}>{entryText(entry)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Next */}
        <button
          onClick={handleNext}
          disabled={escaped}
          style={{ padding: "8px 14px", cursor: escaped ? "not-allowed" : "pointer" }}
        >
          Next
        </button>

        {/* Palette spinner */}
        <select
          value={paletteName}
          onChange={(e) => setPaletteName(e.target.value)}
          style={{ padding: "8px 10px", borderRadius: 8 }}
          title="Choose a palette file from public/palettes/"
        >
          <option value="palette.5">palette.5</option>
          <option value="palette.7">palette.7</option>
          <option value="palette.8">palette.8</option>
          <option value="palette.9">palette.9</option>
          <option value="palette.9a">palette.9a</option>
          <option value="palette.15">palette.15</option>
          <option value="palette.15o">palette.15o</option>
        </select>

        {/* Show mini-map — to the right of palette */}
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <input
            type="checkbox"
            checked={showMini}
            onChange={(e) => setShowMini(e.target.checked)}
          />
          <span>Show local escape-time mini-map</span>
        </label>

        {/* Help — pushed to far right by CSS .help-btn margin-left:auto */}
        <button className="btn help-btn" onClick={() => setHelpOpen(true)}>Help</button>
      </div>

      {/* Mini-map */}
      {showMini && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 12px", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <canvas
              ref={miniRef}
              style={{ width: 180, height: 180, imageRendering: "pixelated", borderRadius: 8, border: "1px solid #999" }}
              title="Local Mandelbrot (escape-time) around your selected c"
            />
            <div style={{ display: "grid", gap: 6 }}>
              <label style={{ fontSize: 12 }}>
                Mini-map resolution:
                <select value={miniRes} onChange={(e) => setMiniRes(parseInt(e.target.value, 10))} style={{ marginLeft: 6 }}>
                  <option value={81}>81×81</option>
                  <option value={101}>101×101</option>
                  <option value={151}>151×151</option>
                </select>
              </label>

              <label style={{ fontSize: 12 }}>
                Half-span (±):
                <input
                  type="number"
                  step="0.05"
                  min="0.05"
                  max="1.0"
                  value={miniSpan}
                  onChange={(e) => setMiniSpan(parseFloat(e.target.value || "0.25"))}
                  style={{ width: 80, marginLeft: 6 }}
                />
              </label>

              <label style={{ fontSize: 12 }}>
                Max iter:
                <input
                  type="number"
                  min="50"
                  max="2000"
                  value={miniMaxIter}
                  onChange={(e) => setMiniMaxIter(parseInt(e.target.value || "200", 10))}
                  style={{ width: 80, marginLeft: 6 }}
                />
              </label>

              <button onClick={() => drawMiniMap()} style={{ padding: "6px 10px" }}>
                Recompute
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Help modal */}
      <HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
}



